import { useEffect, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { isHttpError } from '@/api/http/errors'
import { queryKeys } from '@/api/query-keys'
import {
  type PortraitStudentSearchParams,
  portraitsService,
} from '@/api/services/portraits.service'
import type { PortraitStudent } from '@/api/types/portrait'
import { deriveStudentPlacement } from '@/lib/portraits/portrait-student-placement'
import { logger } from '@/utils/logger'

const PORTRAIT_SEARCH_DEBOUNCE_MS = 300

/**
 * The roster with each student's placed flag. The request asks for `placed=any`, which makes the
 * backend add the server-owned student-level `studentUsedOnPage` (right even when a Grade hides a
 * secondary portrait). That path validates every row, so one malformed row comes back as a 502 for
 * the whole roster. A tray that cannot list anyone is worse than one whose Placed flag is
 * approximate, so on a 502 it asks again without `placed`, which the backend passes straight
 * through, and works the flag out from the rows (`deriveStudentPlacement`). The backend still
 * fails loudly, and the warning names it. Any other failure (auth, network, timeout) is not retried.
 */
async function fetchStudentsWithPlacement(
  params: PortraitStudentSearchParams
): Promise<PortraitStudent[]> {
  try {
    return await portraitsService.searchStudents({ ...params, placed: 'any' })
  } catch (error) {
    if (!isHttpError(error) || error.status !== 502) {
      throw error
    }

    logger.warn(
      'Portraits roster: student placement was refused (502); deriving it from the rows',
      {
        message: error.message,
      }
    )

    return deriveStudentPlacement(await portraitsService.searchStudents(params))
  }
}

export interface PortraitStudentsFilters {
  /** Typed name text ('' = no name criterion). */
  searchName: string
  /** Selected grade id ('' = any grade). */
  gradeId: string
  /** Selected homeroom id ('' = any homeroom). Independent of the grade; both apply (AND). */
  homeroomId?: string
  /** Selected custom category id ('' = any). Independent of the grade and homeroom; all apply
   *  (AND). */
  customId?: string
}

/**
 * The tray's students for the active filters, fuzzy-matched and ranked server-side. The name waits
 * for typing to pause; the grade, homeroom and custom group apply at once. Previous results stay
 * on screen while a new set loads so the gallery and suggestion list don't flash empty between
 * keystrokes.
 *
 * Placed is not a parameter here: the request always carries `placed=any`, which adds the
 * student-level `studentUsedOnPage` flag to every row, and the tray filters Yes / No over those
 * rows (`filterPortraitStudentsByPlaced`) as the other trays do, so toggling it needs no request
 * and the filter and the badge read the same flag.
 */
export function usePortraitStudentsQuery({
  searchName,
  gradeId,
  homeroomId = '',
  customId = '',
}: PortraitStudentsFilters) {
  const trimmed = searchName.trim()
  const [debounced, setDebounced] = useState(trimmed)

  useEffect(() => {
    // Clearing the field applies at once: the name criterion stops restricting the gallery.
    if (trimmed === '') {
      setDebounced('')
      return
    }

    const timeoutId = window.setTimeout(() => setDebounced(trimmed), PORTRAIT_SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [trimmed])

  const query = useQuery({
    queryKey: queryKeys.portraits.students({
      searchName: debounced,
      gradeId,
      homeroomId,
      customId,
    }),
    queryFn: () =>
      fetchStudentsWithPlacement({ searchName: debounced, gradeId, homeroomId, customId }),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  })

  return {
    ...query,
    // The rows are for what is typed and picked right now. False while the debounce is pending
    // (the key still holds the previous name, so `isPlaceholderData` alone misses that window) and
    // while the next set loads. Anything derived from the rows' names, such as the search
    // suggestions, must wait for this; the gallery may keep showing the old rows meanwhile.
    isSearchCurrent: debounced === trimmed && !query.isPlaceholderData,
  }
}
